import "@/App.css";
import { GlobalLayoutStyles } from "@/styles/GlobalLayoutStyles";
import PageLoading from "@components/Loading";
import UpdateNotification from "@components/UpdateNotification";
import { updateUserInfo } from "@redux/slice/user";
import { useAppDispatch, useAppSelector } from "@redux/store";
import Router from "@routers/index";
import { ensureAuthenticated, extractUserInfo, getAndClearReturnUrl, getCurrentProvider, getUserFromToken, handleCallback, logout, normalizeUserInfoResponse } from "@services/auth";
import { AuthBlockReason, onAuthBlocked } from "@services/authEvents";
import { AUTH_ENABLED } from "@services/env";
import { fetch_UserInfo } from "@services/index";
import AuthBlockedPage from "@views/AuthBlockedPage";
import LoginPage from "@views/LoginPage";
import { message } from "antd";
import { useEffect, useRef, useState } from "react";

type AuthState = "loading" | "unauthenticated" | "authenticated" | "blocked";

function App() {
  const dispatch = useAppDispatch();
  const user = useAppSelector(state => state.user);
  const [authState, setAuthState] = useState<AuthState>("loading");
  const [blockReason, setBlockReason] = useState<AuthBlockReason>("unknown");
  const [blockDetail, setBlockDetail] = useState<string | undefined>();
  const [messageApi, contextHolder] = message.useMessage();

  // Track whether we've already handled a block event to avoid duplicate processing
  const hasBlocked = useRef(false);

  // ──────────────────────────────────────────────────────────────────────
  // Subscribe to auth-block events from the axios interceptor.
  // When any API returns 401/402/403, the interceptor emits an event and
  // we transition the whole app to the "blocked" state with the right reason.
  // For 401 (unauthorized), the interceptor also calls logout() which
  // auto-redirects — so the blocked page is a brief flash before redirect.
  // ──────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const unsubscribe = onAuthBlocked(event => {
      if (hasBlocked.current) return; // prevent duplicate transitions
      hasBlocked.current = true;

      setBlockReason(event.reason);
      setBlockDetail(event.message);
      setAuthState("blocked");
    });

    return unsubscribe;
  }, []);

  /**
   * After OAuth succeeds (callback or existing session), fetch the full user
   * profile from the backend.  This gives us:
   *   - roles (needed for RBAC throughout the app)
   *   - activated flag (Cognito operators must be activated by a supervisor)
   *
   * For Cognito users: if `activated !== true` → show the pending-activation page.
   * For IdPrism / Federate users: always proceed (they don't need activation).
   */
  const finalizeAuth = async (tokenUserInfo: ReturnType<typeof extractUserInfo>, provider: string) => {
    try {
      // Fetch full user profile (role, activated, etc.) from the backend.
      // The backend returns a flat, un-enveloped body with its own field names, so
      // normalize it here before merging into the token-derived info.
      const res = await fetch_UserInfo();
      const backendUser = normalizeUserInfoResponse(res);

      // Merge token-derived info with backend-provided info. The backend takes precedence
      // for role/activated, but the token-derived username (the login alias) is canonical
      // and must win over the backend's display-oriented `userName`.
      const mergedUser = { ...tokenUserInfo, ...backendUser, username: tokenUserInfo.username ?? backendUser.username };
      dispatch(updateUserInfo(mergedUser));

      // Cognito users need activation check
      if (provider === "cognito" && backendUser.activated !== true) {
        setBlockReason("pending_activation");
        setAuthState("blocked");
        return;
      }

      setAuthState("authenticated");
    } catch (error) {
      console.error("Failed to fetch user info:", error);
      // If the backend is unreachable, still let the user in with token-only info
      // (roles will default to "none" from initialState)
      dispatch(updateUserInfo(tokenUserInfo));
      setAuthState("authenticated");
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      // ── Auth bypass for development ──
      if (!AUTH_ENABLED) {
        dispatch(
          updateUserInfo({
            username: "dev-user",
            roles: [{ userGroup: "default", role: "administrator" }],
          }),
        );
        setAuthState("authenticated");
        return;
      }

      const pathname = window.location.pathname;

      // Handle OAuth callback from any provider
      if (pathname === "/callback") {
        try {
          const idTokenPayload = await handleCallback();
          const provider = getCurrentProvider();

          // Use provider-aware extraction to normalize user info
          const userInfo = extractUserInfo(idTokenPayload, provider!);

          // Redirect back to the page the user was on before login (or "/" if none)
          const returnUrl = getAndClearReturnUrl();
          window.history.replaceState({}, document.title, returnUrl);

          // Fetch roles + activation status from backend
          await finalizeAuth(userInfo, provider!);
        } catch (error) {
          console.error("Auth callback failed:", error);
          messageApi.error(`Login failed: ${error instanceof Error ? error.message : "Unknown error"}`);
          setAuthState("unauthenticated");
          // Clean up URL on failure too
          window.history.replaceState({}, document.title, "/");
        }
        return;
      }

      // Check if already authenticated. ensureAuthenticated() is async and
      // refresh-aware: if the stored id_token has expired but a valid refresh_token
      // remains, it silently refreshes rather than bouncing the user to login.
      if (await ensureAuthenticated()) {
        const tokenPayload = getUserFromToken();
        const provider = getCurrentProvider();
        if (tokenPayload && provider) {
          // Use provider-aware extraction to normalize user info
          const userInfo = extractUserInfo(tokenPayload, provider);

          // Fetch roles + activation status from backend
          await finalizeAuth(userInfo, provider);
          return;
        }
      }

      // Not authenticated — show login page
      setAuthState("unauthenticated");
    };

    initAuth();
  }, []);

  // Show loading while checking auth state
  if (authState === "loading") {
    return <PageLoading />;
  }

  // Show login page if not authenticated
  if (authState === "unauthenticated" || !user.username) {
    return (
      <>
        {contextHolder}
        <LoginPage />
      </>
    );
  }

  // Auth blocked — show the appropriate blocked page (activation, 401, 403, 402, etc.)
  if (authState === "blocked") {
    return (
      <>
        {contextHolder}
        <AuthBlockedPage reason={blockReason} detail={blockDetail} username={user.username} onLogout={() => logout()} />
      </>
    );
  }

  // Authenticated — render the app
  return (
    <>
      {contextHolder}
      <GlobalLayoutStyles />
      {/* Watches for a newly-deployed version and prompts the user to update (renders null in prod). */}
      <UpdateNotification />
      <Router />
    </>
  );
}

export default App;
